import Link from "next/link";
import {
  ArrowRight,
  Banknote,
  Check,
  Factory,
  GraduationCap,
  Landmark,
} from "lucide-react";
import LandingPageShell from "@/components/landing-page/layout/LandingPageShell";
import PageHero from "@/components/landing-page/PageHero";
import FinalCta from "@/components/landing-page/home/FinalCta";
import Reveal from "@/components/ui/Reveal";
import { BOOK_DEMO_HREF } from "@/lib/marketing-links";

const industries = [
  {
    id: "public-sector",
    peopleTrained: 3000,
    Icon: Landmark,
    name: "Public Sector",
    title: "AI capability for the teams that serve citizens.",
    intro:
      "Government departments and public bodies are being asked to adopt AI responsibly, often with legacy systems, strict procurement and a workforce at very different starting points.",
    challenges: [
      "Large, distributed workforces with uneven digital skills",
      "Strict requirements on data privacy, security and accountability",
      "Pressure to show value for public money",
    ],
    solutions: [
      "A baseline AI assessment across departments and grades",
      "Responsible-AI and policy-aware learning paths for every role",
      "Reporting that shows progress to leadership and oversight bodies",
    ],
    roles: [
      "Policy officers",
      "Case workers",
      "Administrators",
      "Department heads",
      "IT & digital teams",
    ],
    outcomes: [
      { value: "XX%", label: "of staff assessed in the first month" },
      { value: "XX hrs", label: "saved per employee each week" },
      { value: "XX%", label: "improvement in AI capability scores" },
    ],
  },
  {
    id: "education",
    peopleTrained: 5000,
    Icon: GraduationCap,
    name: "Education",
    title: "Prepare educators and staff for teaching in an AI world.",
    intro:
      "Schools, universities and training institutes need faculty and administrators who can use AI well, and who can guide students in using it responsibly.",
    challenges: [
      "Faculty and staff with little time for their own development",
      "Concerns about academic integrity and responsible use",
      "Very different needs across teaching, research and administration",
    ],
    solutions: [
      "Short, practical learning that fits around a teaching schedule",
      "Paths for faculty, administrators and leadership",
      "Expert-reviewed guidance on AI in assessment and the classroom",
    ],
    roles: [
      "Teachers & lecturers",
      "Researchers",
      "Academic leadership",
      "Administrative staff",
      "Student services",
    ],
    outcomes: [
      { value: "XX%", label: "of faculty using AI in course preparation" },
      { value: "XX hrs", label: "saved on administrative work each month" },
      { value: "XX", label: "institutions onboarded" },
    ],
  },
  {
    id: "manufacturing",
    peopleTrained: 1000,
    Icon: Factory,
    name: "Manufacturing",
    title: "From the shop floor to the boardroom, AI that fits the work.",
    intro:
      "Manufacturers are using AI for quality, maintenance, supply chain and planning. The gap is people who know how to apply it to their own line, plant or function.",
    challenges: [
      "Frontline and office teams with very different needs",
      "Training that has to fit around shifts and production targets",
      "AI pilots that stall because teams cannot take them further",
    ],
    solutions: [
      "Role-based paths for operations, engineering, supply chain and management",
      "Practice built on realistic manufacturing scenarios",
      "Skill maps by plant and function to plan where to invest",
    ],
    roles: [
      "Plant managers",
      "Process engineers",
      "Quality teams",
      "Supply chain planners",
      "Operations leads",
    ],
    outcomes: [
      { value: "XX%", label: "reduction in time to adopt new AI tools" },
      { value: "XX", label: "AI use cases identified by teams" },
      { value: "XX%", label: "of managers completing their path" },
    ],
  },
  {
    id: "banking",
    peopleTrained: 500,
    Icon: Banknote,
    name: "Banking and Finance",
    title: "Upskill a regulated workforce, safely and at scale.",
    intro:
      "Banks and financial institutions want the productivity of AI without the risk. That takes people who understand both the tools and the regulatory context they work in.",
    challenges: [
      "Regulatory, compliance and data-security constraints",
      "Large workforces across branches, operations and head office",
      "The need to show regulators and boards that AI use is controlled",
    ],
    solutions: [
      "Learning paths that build in compliance and risk awareness",
      "Tracks for retail, risk, compliance, operations and technology",
      "Expert-validated skills and audit-ready progress reporting",
    ],
    roles: [
      "Relationship managers",
      "Risk & compliance",
      "Operations",
      "Analysts",
      "Technology teams",
    ],
    outcomes: [
      { value: "XX%", label: "of employees assessed across branches" },
      { value: "XX%", label: "faster turnaround on routine processes" },
      { value: "XX%", label: "improvement in AI capability scores" },
    ],
  },
] as const;

const SHOW_OUTCOMES = false;

const tones = {
  dark: {
    section: "bg-mkt-inarax-black",
    iconBadge: "bg-white/10 text-white ring-1 ring-white/25",
    label: "text-white",
    heading: "text-white",
    body: "text-white/75",
    subheading: "text-white",
    chip: "bg-white/10 text-white",
    link: "text-white border-white",
    card: "border border-white/15 bg-white/[0.04]",
    cardHeading: "text-white",
    cardItem: "text-white/85",
    check: "text-mkt-on-primary-container",
    stats: "bg-white/[0.06] border border-white/15",
    statLabel: "text-white/75",
    statValue: "text-white",
  },
  light: {
    section: "bg-white",
    iconBadge: "bg-mkt-primary/10 text-mkt-primary",
    label: "text-mkt-primary",
    heading: "text-mkt-inarax-black",
    body: "text-mkt-on-surface-variant",
    subheading: "text-mkt-primary",
    chip: "bg-mkt-primary-container/[0.08] text-mkt-on-surface",
    link: "text-mkt-primary border-mkt-primary",
    card: "border border-mkt-primary/20 bg-white shadow-[0_4px_16px_rgba(0,60,185,0.10)]",
    cardHeading: "text-mkt-inarax-black",
    cardItem: "text-mkt-on-surface",
    check: "text-mkt-primary",
    stats: "bg-mkt-primary-container/[0.06] border border-mkt-primary/15",
    statLabel: "text-mkt-on-surface-variant",
    statValue: "text-mkt-primary-container",
  },
} as const;

export default function IndustriesPage() {
  return (
    <LandingPageShell>
      <PageHero
        eyebrow="Industries"
        title="AI capability built for your industry."
        description="Every industry adopts AI differently. inaraX has worked with teams in the public sector, education, manufacturing and banking, and shapes each program around their roles, rules and goals."
      >
        <ul className="list-none pl-0 ml-0 mt-size-md flex flex-wrap gap-size-sm">
          {industries.map(({ id, name, Icon }) => (
            <li key={id}>
              <Link
                href={`#${id}`}
                className="inline-flex items-center gap-2 rounded-full border border-mkt-primary/30 bg-white px-4 py-2 font-label-md text-label-md font-bold text-mkt-primary transition-colors hover:bg-mkt-primary-container hover:text-mkt-on-primary"
              >
                <Icon
                  aria-hidden="true"
                  strokeWidth={1.5}
                  className="h-4 w-4"
                />
                {name}
              </Link>
            </li>
          ))}
        </ul>
      </PageHero>

      {industries.map(
        (
          {
            id,
            Icon,
            name,
            title,
            intro,
            challenges,
            solutions,
            roles,
            peopleTrained,
            outcomes,
          },
          index,
        ) => {
          const tone = index % 2 === 0 ? tones.dark : tones.light;
          return (
            <section
              key={id}
              id={id}
              aria-labelledby={`${id}-heading`}
              className={`scroll-mt-20 px-margin-mobile md:px-margin-desktop py-size-xl ${tone.section}`}
            >
              <div className="max-w-[1440px] mx-auto flex flex-col gap-size-lg">
                <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-size-lg">
                  <Reveal>
                    <span
                      className={`flex h-12 w-12 items-center justify-center rounded-full mb-size-md ${tone.iconBadge}`}
                    >
                      <Icon
                        aria-hidden="true"
                        strokeWidth={1.5}
                        className="h-6 w-6"
                      />
                    </span>
                    <div className="flex items-center gap-base mb-size-sm">
                      <span className="w-12 h-[1px] bg-mkt-primary" />
                      <span
                        className={`font-label-md text-label-md uppercase tracking-widest ${tone.label}`}
                      >
                        {name}
                      </span>
                    </div>
                    <h2
                      id={`${id}-heading`}
                      className={`font-display-lg text-[clamp(28px,3vw,44px)] leading-[1.15] tracking-[-0.02em] font-bold mb-size-md ${tone.heading}`}
                    >
                      {title}
                    </h2>
                    <p className={`font-body-lg text-body-lg ${tone.body}`}>
                      {intro}
                    </p>

                    <h3
                      className={`mt-size-md font-label-md text-label-md uppercase tracking-widest mb-size-sm ${tone.subheading}`}
                    >
                      Roles we train
                    </h3>
                    <ul className="list-none pl-0 ml-0 flex flex-wrap gap-2">
                      {roles.map((role) => (
                        <li
                          key={role}
                          className={`rounded-full px-3 py-1 font-body-md text-body-md ${tone.chip}`}
                        >
                          {role}
                        </li>
                      ))}
                    </ul>

                    <Link
                      href={BOOK_DEMO_HREF}
                      className={`group mt-size-md inline-flex items-center gap-1.5 font-label-md text-label-md font-bold border-b-2 ${tone.link}`}
                    >
                      Talk to us about {name.toLowerCase()}
                      <ArrowRight
                        aria-hidden="true"
                        className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1"
                      />
                    </Link>
                  </Reveal>

                  <Reveal
                    delayMs={100}
                    className="grid grid-cols-1 sm:grid-cols-2 gap-size-md content-start"
                  >
                    {[
                      { heading: "The challenge", points: challenges },
                      { heading: "How inaraX helps", points: solutions },
                    ].map(({ heading, points }) => (
                      <div
                        key={heading}
                        className={`rounded-xl p-size-md ${tone.card}`}
                      >
                        <h3
                          className={`font-headline-md text-headline-sm mb-size-sm ${tone.cardHeading}`}
                        >
                          {heading}
                        </h3>
                        <ul className="list-none pl-0 ml-0 flex flex-col gap-size-sm">
                          {points.map((point) => (
                            <li
                              key={point}
                              className={`flex items-start gap-size-sm font-body-md text-body-md ${tone.cardItem}`}
                            >
                              <Check
                                aria-hidden="true"
                                className={`mt-0.5 h-5 w-5 shrink-0 ${tone.check}`}
                              />
                              {point}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </Reveal>
                </div>

                <Reveal className="grid grid-cols-1 gap-size-md">
                  <dl
                    className={`grid grid-cols-1 ${SHOW_OUTCOMES ? "sm:grid-cols-4" : "w-fit pr-size-lg"} gap-size-md rounded-xl p-size-md ${tone.stats}`}
                  >
                    <div className="flex flex-col-reverse justify-center">
                      <dt
                        className={`font-body-md text-body-md ${tone.statLabel}`}
                      >
                        people trained in {name.toLowerCase()}
                      </dt>
                      <dd
                        className={`ml-0 font-display-lg text-[clamp(36px,3.4vw,56px)] leading-none font-bold ${tone.statValue}`}
                      >
                        {peopleTrained.toLocaleString("en-US")}+
                      </dd>
                    </div>
                  </dl>
                </Reveal>
              </div>
            </section>
          );
        },
      )}

      <FinalCta />
    </LandingPageShell>
  );
}
